import { CourierInboxSocket } from '../socket/courier-inbox-socket';
import { CourierGetInboxMessagesQueryFilter, CourierGetInboxMessagesResponse } from '../types/inbox';
import { graphql } from '../utils/request';
import { Client } from './client';
import { CourierClientOptions } from './courier-client';

export class InboxClient extends Client {

  readonly socket: CourierInboxSocket;

  constructor(options: CourierClientOptions) {
    super(options);
    this.socket = new CourierInboxSocket(options);
  }

  /**
   * Get paginated messages
   * @param paginationLimit - Number of messages to return per page (default: 24)
   * @param startCursor - Cursor for pagination
   * @returns Promise resolving to paginated messages response
   */
  public async getMessages(props?: {
    paginationLimit?: number;
    startCursor?: string;
    filter?: CourierGetInboxMessagesQueryFilter;
  }): Promise<CourierGetInboxMessagesResponse> {
    const filter = props?.filter || {};
    const filterParams = this.createFilterParams(filter);
    const unreadCountFilterParams = this.createUnreadCountFilterParams(filter)

    const query = `
      query GetInboxMessages(
        $params: FilterParamsInput = ${filterParams}
        $unreadCountParams: FilterParamsInput = ${unreadCountFilterParams}
        $limit: Int = ${props?.paginationLimit ?? 24}
        $after: String ${props?.startCursor ? `= "${props.startCursor}"` : ''}
      ) {
        count: count(params: $params)
        unreadCount: count(params: $unreadCountParams)
        messages(params: $params, limit: $limit, after: $after) {
          totalCount
          pageInfo {
            startCursor
            hasNextPage
          }
          nodes {
            messageId
            read
            archived
            created
            opened
            title
            preview
            data
            tags
            trackingIds {
              clickTrackingId
            }
            actions {
              content
              data
              href
            }
          }
        }
      }
    `;

    return await graphql({
      options: this.options,
      query,
      headers: {
        'x-courier-user-id': this.options.userId,
        'Authorization': `Bearer ${this.options.accessToken}`
      },
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Get unread counts for multiple filters in a single query.
   *
   * @param filtersMap - Map of dataset ID to filter
   * @returns Promise resolving to map of dataset ID to unread count
   */
  public async getUnreadCounts(
    filtersMap: Record<string, CourierGetInboxMessagesQueryFilter>
  ): Promise<Record<string, number>> {
    const result: Record<string, number> = {};
    
    // Separate filters: those with status 'read' can be optimized to return 0,
    // others need to be queried
    const filtersToQuery: Record<string, CourierGetInboxMessagesQueryFilter> = {};
    
    for (const [datasetId, filter] of Object.entries(filtersMap)) {
      if (filter.status === 'read') {
        // Optimization: if status is 'read', unread count is always 0
        // TODO: We should remove this at somepoint. There is a bug in the backend that is returning more than 0 for read messages.
        result[datasetId] = 0;
      } else {
        // This filter needs to be queried
        filtersToQuery[datasetId] = filter;
      }
    }

    // If no filters need to be queried, return early
    if (Object.keys(filtersToQuery).length === 0) {
      return result;
    }

    // Build query variables and field aliases for remaining filters
    const variables: string[] = [];
    const fields: string[] = [];

    // Map from the user-provided dataset ID to a GraphQL-safe alias.
    // We keep track of this mapping so it can be reversed and we can return
    // a mapping from user-provided dataset ID to unread count.
    const sanitizedIdMapping: Record<string, string> = {};

    for (const [datasetId, filter] of Object.entries(filtersToQuery)) {
      const sanitizedId = InboxClient.sanitizeGraphQLIdentifier(datasetId);
      sanitizedIdMapping[sanitizedId] = datasetId;

      const unreadCountFilterParams = this.createUnreadCountFilterParams(filter);
      variables.push(`$${sanitizedId}: FilterParamsInput = ${unreadCountFilterParams}`);
      fields.push(`${sanitizedId}: count(params: $${sanitizedId})`);
    }

    const query = `
      query GetUnreadCounts(
        ${variables.join('\n')}
      ) {
        ${fields.join('\n')}
      }
    `;

    const response = await graphql({
      options: this.options,
      query,
      headers: {
        'x-courier-user-id': this.options.userId,
        'Authorization': `Bearer ${this.options.accessToken}`
      },
      url: this.options.apiUrls.inbox.graphql,
    });

    // Parse response data back into a map of user-provided ID to unread count
    // from the GraphQL-safe aliases used in the query.
    if (response.data) {
      for (const [sanitizedId, originalId] of Object.entries(sanitizedIdMapping)) {
        result[originalId] = response.data[sanitizedId] ?? 0;
      }
    }

    return result;
  }

  /**
   * Get paginated archived messages
   * @param paginationLimit - Number of messages to return per page (default: 24)
   * @param startCursor - Cursor for pagination
   * @returns Promise resolving to paginated archived messages response
   *
   * @deprecated - replace usages with {@link InboxClient.getMessages}, passing the filter `{ archived: true }`
   */
  public async getArchivedMessages(props?: { paginationLimit?: number; startCursor?: string; }): Promise<CourierGetInboxMessagesResponse> {
    return this.getMessages({
      paginationLimit: props?.paginationLimit,
      startCursor: props?.startCursor,
      filter: { archived: true }
    });
  }

  /**
   * Get unread message count
   * @returns Promise resolving to number of unread messages
   */
  public async getUnreadMessageCount(): Promise<number> {
    const query = `
      query GetMessages {
        count(params: { status: "unread" ${this.options.tenantId ? `, accountId: "${this.options.tenantId}"` : ''} })
      }
    `;

    const response = await graphql({
      options: this.options,
      query,
      headers: {
        'x-courier-user-id': this.options.userId,
        'Authorization': `Bearer ${this.options.accessToken}`
      },
      url: this.options.apiUrls.inbox.graphql,
    });

    return response.data?.count ?? 0;
  }

  /**
   * Track a click event
   * @param messageId - ID of the message
   * @param trackingId - ID for tracking the click
   * @returns Promise resolving when click is tracked
   */
  public async click(props: { messageId: string, trackingId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        clicked(messageId: "${props.messageId}", trackingId: "${props.trackingId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Mark a message as read
   * @param messageId - ID of the message to mark as read
   * @returns Promise resolving when message is marked as read
   */
  public async read(props: { messageId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        read(messageId: "${props.messageId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Mark a message as unread
   * @param messageId - ID of the message to mark as unread
   * @returns Promise resolving when message is marked as unread
   */
  public async unread(props: { messageId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        unread(messageId: "${props.messageId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Mark a message as opened
   * @param messageId - ID of the message to mark as opened
   * @returns Promise resolving when message is marked as opened
   */
  public async open(props: { messageId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        opened(messageId: "${props.messageId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Mark multiple messages as opened in a single request.
   * @param messageIds - IDs of the messages to mark as opened
   * @returns Promise resolving when all messages are marked as opened
   */
  public async batchOpen(messageIds: string[]): Promise<void> {
    if (messageIds.length === 0) return;

    if (messageIds.length === 1) {
      return this.open({ messageId: messageIds[0] });
    }

    const mutations = messageIds.map((id, index) =>
      `open_${index}: opened(messageId: "${id}")`
    );

    const query = `
      mutation BatchTrackEvents {
        ${mutations.join('\n        ')}
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Archive a message
   * @param messageId - ID of the message to archive
   * @returns Promise resolving when message is archived
   */
  public async archive(props: { messageId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        archive(messageId: "${props.messageId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Unarchive a message
   * @param messageId - ID of the message to unarchive
   * @returns Promise resolving when message is unarchived
   */
  public async unarchive(props: { messageId: string }): Promise<void> {
    const query = `
      mutation TrackEvent {
        unarchive(messageId: "${props.messageId}")
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Mark all messages as read
   * @returns Promise resolving when all messages are marked as read
   */
  public async readAll(): Promise<void> {
    const query = `
      mutation TrackEvent {
        markAllRead
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Archive all read messages.
   */
  public async archiveRead(): Promise<void> {
    const query = `
      mutation TrackEvent {
        archiveRead
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Archive all read messages.
   */
  public async archiveAll(): Promise<void> {
    const query = `
      mutation TrackEvent {
        archiveAll
      }
    `;

    const headers: Record<string, string> = {
      'x-courier-user-id': this.options.userId,
      'Authorization': `Bearer ${this.options.accessToken}`
    };

    if (this.options.connectionId) {
      headers['x-courier-client-source-id'] = this.options.connectionId;
    }

    await graphql({
      options: this.options,
      query,
      headers,
      url: this.options.apiUrls.inbox.graphql,
    });
  }

  /**
   * Create FilterParamsInput for the given filters.
   *
   * @param filter - the filtering options to include in the output
   * @returns the FilterParamsInput to pass to a GraphQL query for messages
   */
  private createFilterParams(filter: CourierGetInboxMessagesQueryFilter) {
    const parts = []

    if (this.options.tenantId) {
      parts.push(`accountId: "${this.options.tenantId}"`);
    }

    if (filter.tags) {
      parts.push(`tags: [${filter.tags.map(tag => `"${tag}"`).join(',')}]`);
    }

    if (filter.status) {
      parts.push(`status: "${filter.status}"`);
    }

    if (filter.archived) {
      parts.push(`archived: ${filter.archived}`);
    }

    if (filter.from) {
      parts.push(`from: "${filter.from}"`);
    }

    return `{ ${parts.join(',')} }`;
  }

  /**
   * Create FilterParamsInput for the unread message count.
   *
   * The status: "unread" filter is only added if status is unset. This is because:
   *  - If status is "unread", the params already include the filter that would be added.
   *  - If status is "read", the unread count for the dataset would be a different set
   *    of messages rather than a count of the unread subset.
   */
  private createUnreadCountFilterParams(filter: CourierGetInboxMessagesQueryFilter) {
    if (!filter.status) {
      return this.createFilterParams({ ...filter, status: 'unread' });
    }

    return this.createFilterParams(filter);
  }

  /**
   * Sanitize dataset IDs for use as GraphQL identifiers.
   *
   * GraphQL identifiers must contain only alphanumerics/underscores and begin with a letter or underscore.
   * https://spec.graphql.org/draft/#sec-Names
   */
  private static sanitizeGraphQLIdentifier(id: string): string {
    // Prepend with id and
    // replace _ (underscore) with double underscore (effectively escaping it) and
    // replace - (hyphen) with underscore
    return `id_${id.replace(/_/g, '__').replace(/-/g, '_')}`
  }

}
